import type { AnyBotEngine } from './bots/types.js';
import type { AnyGameEngine, GameType } from './core/types.js';
import { callBreakBot } from './games/callbreak/bot.js';
import { callBreakEngine } from './games/callbreak/engine.js';
import { fiveHundredBot } from './games/fivehundred/bot.js';
import { fiveHundredEngine } from './games/fivehundred/engine.js';
import { judgementBot } from './games/judgement/bot.js';
import { euchreBot } from './games/euchre/bot.js';
import { ginBot } from './games/gin/bot.js';
import { ginEngine } from './games/gin/engine.js';
import { euchreEngine } from './games/euchre/engine.js';
import { rummyBot } from './games/rummy/bot.js';
import { rummyEngine } from './games/rummy/engine.js';
import { judgementEngine } from './games/judgement/engine.js';
import { heartsBot } from './games/hearts/bot.js';
import { heartsEngine } from './games/hearts/engine.js';
import { marriageBot } from './games/marriage/bot.js';
import { marriageEngine } from './games/marriage/engine.js';
import { pokerBot } from './games/poker/bot.js';
import { pokerEngine } from './games/poker/engine.js';
import { spadesBot } from './games/spades/bot.js';
import { spadesEngine } from './games/spades/engine.js';
import { bluffBot } from './games/bluff/bot.js';
import { bluffEngine } from './games/bluff/engine.js';
import { baccaratBot } from './games/baccarat/bot.js';
import { baccaratEngine } from './games/baccarat/engine.js';
import { scopaBot } from './games/scopa/bot.js';
import { scopaEngine } from './games/scopa/engine.js';
import { sevensBot } from './games/sevens/bot.js';
import { sevensEngine } from './games/sevens/engine.js';
import { bridgeBot } from './games/bridge/bot.js';
import { bridgeEngine } from './games/bridge/engine.js';
import { andarBaharBot } from './games/andarbahar/bot.js';
import { andarBaharEngine } from './games/andarbahar/engine.js';
import { pinochleBot } from './games/pinochle/bot.js';
import { pinochleEngine } from './games/pinochle/engine.js';
import { bhabhiBot } from './games/bhabhi/bot.js';
import { bhabhiEngine } from './games/bhabhi/engine.js';
import { blackjackBot } from './games/blackjack/bot.js';
import { blackjackEngine } from './games/blackjack/engine.js';
import { cribbageBot } from './games/cribbage/bot.js';
import { cribbageEngine } from './games/cribbage/engine.js';
import { crazyEightsBot } from './games/crazyeights/bot.js';
import { crazyEightsEngine } from './games/crazyeights/engine.js';
import { twentyNineBot } from './games/twentynine/bot.js';
import { twentyNineEngine } from './games/twentynine/engine.js';
import { tarneebBot } from './games/tarneeb/bot.js';
import { tarneebEngine } from './games/tarneeb/engine.js';
import { teenPattiBot } from './games/teenpatti/bot.js';
import { teenPattiEngine } from './games/teenpatti/engine.js';
import { dragonTigerBot } from './games/dragontiger/bot.js';
import { dragonTigerEngine } from './games/dragontiger/engine.js';
import { flushBot } from './games/flush/bot.js';
import { flushEngine } from './games/flush/engine.js';
import { courtPieceBot } from './games/courtpiece/bot.js';
import { courtPieceEngine } from './games/courtpiece/engine.js';
import { dehlaPakadBot } from './games/dehlapakad/bot.js';
import { dehlaPakadEngine } from './games/dehlapakad/engine.js';
import { whistBot } from './games/whist/bot.js';
import { whistEngine } from './games/whist/engine.js';
import { seepBot } from './games/seep/bot.js';
import { seepEngine } from './games/seep/engine.js';
import { beloteBot } from './games/belote/bot.js';
import { beloteEngine } from './games/belote/engine.js';
import { durakBot } from './games/durak/bot.js';
import { durakEngine } from './games/durak/engine.js';
import { presidentBot } from './games/president/bot.js';
import { presidentEngine } from './games/president/engine.js';
import { briscolaBot } from './games/briscola/bot.js';
import { briscolaEngine } from './games/briscola/engine.js';
import { casinoWarBot } from './games/casinowar/bot.js';
import { casinoWarEngine } from './games/casinowar/engine.js';
import { threeCardPokerBot } from './games/threecardpoker/bot.js';
import { threeCardPokerEngine } from './games/threecardpoker/engine.js';
import { goFishBot } from './games/gofish/bot.js';
import { goFishEngine } from './games/gofish/engine.js';
import { tressetteBot } from './games/tressette/bot.js';
import { tressetteEngine } from './games/tressette/engine.js';
import { redDogBot } from './games/reddog/bot.js';
import { redDogEngine } from './games/reddog/engine.js';
import { canastaBot } from './games/canasta/bot.js';
import { canastaEngine } from './games/canasta/engine.js';
import { skatBot } from './games/skat/bot.js';
import { skatEngine } from './games/skat/engine.js';
import { paiGowBot } from './games/paigow/bot.js';
import { paiGowEngine } from './games/paigow/engine.js';
import { bigTwoBot } from './games/bigtwo/bot.js';
import { bigTwoEngine } from './games/bigtwo/engine.js';
import { warBot } from './games/war/bot.js';
import { warEngine } from './games/war/engine.js';
import { rummy500Bot } from './games/rummy500/bot.js';
import { rummy500Engine } from './games/rummy500/engine.js';
import { bragBot } from './games/brag/bot.js';
import { bragEngine } from './games/brag/engine.js';
import { pitchBot } from './games/pitch/bot.js';
import { pitchEngine } from './games/pitch/engine.js';
import { hazariBot } from './games/hazari/bot.js';
import { hazariEngine } from './games/hazari/engine.js';

export interface GameModule {
  engine: AnyGameEngine;
  bot: AnyBotEngine;
}

/** Single place mapping a game type to its engine + bot strategy. */
export const GAME_REGISTRY: Record<GameType, GameModule> = {
  CALL_BREAK: { engine: callBreakEngine, bot: callBreakBot },
  SPADES: { engine: spadesEngine, bot: spadesBot },
  TARNEEB: { engine: tarneebEngine, bot: tarneebBot },
  TWENTY_NINE: { engine: twentyNineEngine, bot: twentyNineBot },
  CRAZY_EIGHTS: { engine: crazyEightsEngine, bot: crazyEightsBot },
  CRIBBAGE: { engine: cribbageEngine, bot: cribbageBot },
  BLACKJACK: { engine: blackjackEngine, bot: blackjackBot },
  BHABHI: { engine: bhabhiEngine, bot: bhabhiBot },
  PINOCHLE: { engine: pinochleEngine, bot: pinochleBot },
  ANDAR_BAHAR: { engine: andarBaharEngine, bot: andarBaharBot },
  BRIDGE: { engine: bridgeEngine, bot: bridgeBot },
  SEVENS: { engine: sevensEngine, bot: sevensBot },
  SCOPA: { engine: scopaEngine, bot: scopaBot },
  BACCARAT: { engine: baccaratEngine, bot: baccaratBot },
  BLUFF: { engine: bluffEngine, bot: bluffBot },
  DRAGON_TIGER: { engine: dragonTigerEngine, bot: dragonTigerBot },
  FLUSH: { engine: flushEngine, bot: flushBot },
  COURT_PIECE: { engine: courtPieceEngine, bot: courtPieceBot },
  DEHLA_PAKAD: { engine: dehlaPakadEngine, bot: dehlaPakadBot },
  WHIST: { engine: whistEngine, bot: whistBot },
  SEEP: { engine: seepEngine, bot: seepBot },
  BELOTE: { engine: beloteEngine, bot: beloteBot },
  DURAK: { engine: durakEngine, bot: durakBot },
  PRESIDENT: { engine: presidentEngine, bot: presidentBot },
  BRISCOLA: { engine: briscolaEngine, bot: briscolaBot },
  CASINO_WAR: { engine: casinoWarEngine, bot: casinoWarBot },
  THREE_CARD_POKER: { engine: threeCardPokerEngine, bot: threeCardPokerBot },
  GO_FISH: { engine: goFishEngine, bot: goFishBot },
  TRESSETTE: { engine: tressetteEngine, bot: tressetteBot },
  RED_DOG: { engine: redDogEngine, bot: redDogBot },
  CANASTA: { engine: canastaEngine, bot: canastaBot },
  SKAT: { engine: skatEngine, bot: skatBot },
  PAI_GOW: { engine: paiGowEngine, bot: paiGowBot },
  BIG_TWO: { engine: bigTwoEngine, bot: bigTwoBot },
  WAR: { engine: warEngine, bot: warBot },
  RUMMY_500: { engine: rummy500Engine, bot: rummy500Bot },
  BRAG: { engine: bragEngine, bot: bragBot },
  PITCH: { engine: pitchEngine, bot: pitchBot },
  HAZARI: { engine: hazariEngine, bot: hazariBot },
  HEARTS: { engine: heartsEngine, bot: heartsBot },
  FIVE_HUNDRED: { engine: fiveHundredEngine, bot: fiveHundredBot },
  JUDGEMENT: { engine: judgementEngine, bot: judgementBot },
  RUMMY: { engine: rummyEngine, bot: rummyBot },
  EUCHRE: { engine: euchreEngine, bot: euchreBot },
  GIN_RUMMY: { engine: ginEngine, bot: ginBot },
  MARRIAGE: { engine: marriageEngine, bot: marriageBot },
  TEEN_PATTI: { engine: teenPattiEngine, bot: teenPattiBot },
  POKER: { engine: pokerEngine, bot: pokerBot },
};

export function getGameModule(type: GameType): GameModule {
  const m = GAME_REGISTRY[type];
  if (!m) throw new Error(`Unknown game type ${type}`);
  return m;
}

/** The validated engine config for a table: its rules JSON plus seat counts, on top of the game's defaults. */
export function buildEngineConfig(
  gameType: GameType,
  rules: Record<string, unknown>,
  maxPlayers: number,
  minPlayers: number,
  overrides: Record<string, unknown> = {},
): unknown {
  return getGameModule(gameType).engine.normalizeConfig({ ...rules, maxPlayers, minPlayers, ...overrides });
}
